/*
 * Eureka wishlist - client-side store + UI controllers.
 *
 * MECHANISM (driver/merchant decision 2026-07-24): customer metafield + localStorage,
 * implemented theme-native as:
 *   - localStorage is the WRITABLE source of truth (works for guests AND logged-in
 *     customers). Entries are stored under the key `theme:wishlist` as a JSON list of
 *     { pid, handle, id (variant id | null), qty }.
 *   - The customer metafield `custom.wishlist` is READ defensively on boot to hydrate
 *     the store when a logged-in customer has a merchant-provisioned/synced list. The
 *     metafield is injected by Liquid ONLY when it exists (guarded) - see
 *     `sections/main-wishlist.liquid`. It currently does NOT exist in the store (no
 *     CUSTOMER metafield definition), so hydration degrades gracefully to empty.
 *   - We DO NOT write customer metafields and perform NO store mutation. A Liquid theme
 *     cannot write customer metafields client-side, and creating the definition is a
 *     store mutation (safety gate). Metafield WRITE-BACK / cross-device sync is an infra
 *     follow-up: it needs a `custom.wishlist` CUSTOMER metafield definition (storefront
 *     read access) plus an app proxy or the Customer Account API. The READ path below is
 *     wired now so it activates the moment the merchant provisions the metafield.
 */
(function () {
  'use strict';

  if (window.__wishlistInit) return;
  window.__wishlistInit = true;

  var KEY = 'theme:wishlist';
  var EVENT = 'wishlist:updated';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function normalizeEntry(raw) {
    if (raw == null) return null;
    if (typeof raw === 'object') {
      var pid = raw.pid || raw.product_id || raw.productId || raw.id;
      if (!pid) return null;
      return {
        pid: String(pid),
        handle: raw.handle || '',
        id: raw.id != null && raw.id !== '' ? String(raw.id) : null,
        qty: parseInt(raw.qty, 10) > 0 ? parseInt(raw.qty, 10) : 1,
      };
    }
    return { pid: String(raw), handle: '', id: null, qty: 1 };
  }

  var Store = {
    read: function () {
      try {
        var list = JSON.parse(localStorage.getItem(KEY) || '[]');
        return Array.isArray(list) ? list : [];
      } catch (e) {
        return [];
      }
    },
    write: function (list) {
      try {
        localStorage.setItem(KEY, JSON.stringify(list));
      } catch (e) {
        /* storage full / unavailable - degrade silently */
      }
      document.dispatchEvent(new CustomEvent(EVENT, { detail: { items: list } }));
    },
    items: function () {
      return this.read();
    },
    count: function () {
      return this.read().length;
    },
    hasProduct: function (pid) {
      return this.read().some(function (i) {
        return String(i.pid) === String(pid);
      });
    },
    getProduct: function (pid) {
      return this.read().filter(function (i) {
        return String(i.pid) === String(pid);
      })[0];
    },
    toggleProduct: function (raw) {
      var entry = normalizeEntry(raw);
      if (!entry) return false;
      var list = this.read();
      var idx = list.findIndex(function (i) {
        return String(i.pid) === String(entry.pid);
      });
      if (idx > -1) {
        list.splice(idx, 1);
        this.write(list);
        return false;
      }
      list.push(entry);
      this.write(list);
      return true;
    },
    upsertVariant: function (raw) {
      var entry = normalizeEntry(raw);
      if (!entry) return;
      var list = this.read();
      var idx = list.findIndex(function (i) {
        return String(i.pid) === String(entry.pid);
      });
      if (idx > -1) {
        list[idx] = Object.assign({}, list[idx], entry);
      } else {
        list.push(entry);
      }
      this.write(list);
    },
    removeProduct: function (pid) {
      var list = this.read().filter(function (i) {
        return String(i.pid) !== String(pid);
      });
      this.write(list);
    },
    hydrateFromMetafield: function (mfList) {
      if (!Array.isArray(mfList) || !mfList.length) return;
      var list = this.read();
      var seen = {};
      list.forEach(function (i) {
        seen[String(i.pid)] = true;
      });
      var changed = false;
      mfList.forEach(function (raw) {
        var entry = normalizeEntry(raw);
        if (entry && !seen[String(entry.pid)]) {
          list.push(entry);
          seen[String(entry.pid)] = true;
          changed = true;
        }
      });
      if (changed) this.write(list);
    },
  };
  window.WishlistStore = Store;

  /* Reflect the pressed state of every card heart against the store. */
  function reflectToggles(scope) {
    var container = scope || document;
    container.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var pid = btn.getAttribute('data-product-id');
      btn.setAttribute('aria-pressed', Store.hasProduct(pid) ? 'true' : 'false');
    });
  }

  /* Single delegated owner for the card heart toggle.
     - Already saved -> remove directly (no drawer needed to remove).
     - Not saved, product needs a variant (size/colour) and the Add drawer exists ->
       open the add-size-select drawer so the shopper picks a size before saving.
     - Otherwise (single-variant product) -> add directly. */
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-wishlist-toggle]');
    if (!toggle) return;
    e.preventDefault();
    var pid = toggle.getAttribute('data-product-id');
    if (
      !Store.hasProduct(pid) &&
      toggle.getAttribute('data-wl-requires-variant') === 'true' &&
      document.getElementById(DRAWERS.add)
    ) {
      openWishlistDrawer('add', toggle);
      return;
    }
    var added = Store.toggleProduct({
      pid: pid,
      handle: toggle.getAttribute('data-product-handle') || '',
      id: toggle.getAttribute('data-variant-id') || null,
    });
    toggle.setAttribute('aria-pressed', added ? 'true' : 'false');
  });

  /* Keep hearts + counts in sync after any store change. */
  document.addEventListener(EVENT, function () {
    reflectToggles(document);
  });

  /* Live count badge (header). */
  if (!customElements.get('wishlist-count')) {
    customElements.define(
      'wishlist-count',
      class extends HTMLElement {
        connectedCallback() {
          this._render();
          this._onUpdate = this._render.bind(this);
          document.addEventListener(EVENT, this._onUpdate);
        }
        disconnectedCallback() {
          document.removeEventListener(EVENT, this._onUpdate);
        }
        _render() {
          var count = Store.count();
          this.textContent = count > 99 ? '99+' : String(count);
          if (count > 0) {
            this.removeAttribute('hidden');
          } else {
            this.setAttribute('hidden', '');
          }
        }
      }
    );
  }

  /* Fetch a section's rendered HTML for a product via the Section Rendering API. */
  function fetchProductSection(handle, sectionId) {
    var url = root() + 'products/' + encodeURIComponent(handle) + '?section_id=' + encodeURIComponent(sectionId);
    return fetch(url).then(function (res) {
      if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
      return res.text();
    });
  }

  function parseFragment(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  /* Wishlist page grid - hydrates each stored item into a real product tile. */
  if (!customElements.get('wishlist-page')) {
    customElements.define(
      'wishlist-page',
      class extends HTMLElement {
        connectedCallback() {
          this.grid = this.querySelector('[data-wl-grid]');
          this.emptyState = this.querySelector('[data-wl-empty]');
          this.loadingEl = this.querySelector('[data-wl-loading]');
          this.tileSection = this.getAttribute('data-tile-section');
          this._onUpdate = this._onStoreChange.bind(this);
          this._rendered = '';
          this.render();
          document.addEventListener(EVENT, this._onUpdate);
        }
        disconnectedCallback() {
          document.removeEventListener(EVENT, this._onUpdate);
        }
        _onStoreChange() {
          /* Only re-render when the set of product ids actually changed. */
          var signature = Store.items()
            .map(function (i) {
              return i.pid + ':' + (i.id || '');
            })
            .join(',');
          if (signature !== this._rendered) this.render();
        }
        render() {
          var items = Store.items();
          this._rendered = items
            .map(function (i) {
              return i.pid + ':' + (i.id || '');
            })
            .join(',');

          if (!items.length) {
            if (this.grid) this.grid.innerHTML = '';
            if (this.emptyState) this.emptyState.removeAttribute('hidden');
            if (this.loadingEl) this.loadingEl.setAttribute('hidden', '');
            return;
          }
          if (this.emptyState) this.emptyState.setAttribute('hidden', '');
          if (!this.grid || !this.tileSection) return;
          if (this.loadingEl) this.loadingEl.removeAttribute('hidden');

          var self = this;
          var withHandle = items.filter(function (i) {
            return i.handle;
          });
          Promise.all(
            withHandle.map(function (item) {
              return fetchProductSection(item.handle, self.tileSection)
                .then(function (html) {
                  var node = parseFragment(html, '[data-wl-tile-root]');
                  return node ? { item: item, node: node } : null;
                })
                .catch(function () {
                  return null;
                });
            })
          ).then(function (results) {
            self.grid.innerHTML = '';
            results.forEach(function (result) {
              if (!result) return;
              var li = document.createElement('li');
              li.className = 'wishlist__item';
              li.appendChild(result.node);
              /* Apply the stored variant to the tile once mounted. */
              if (result.item.id) {
                var idInput = li.querySelector('.product-variant-id, [data-wl-tile-variant-id]');
                if (idInput && 'value' in idInput) idInput.value = result.item.id;
                li.querySelectorAll('[data-wl-edit], [data-wl-tile-root]').forEach(function (el) {
                  el.setAttribute('data-variant-id', result.item.id);
                });
              }
              self.grid.appendChild(li);
            });
            if (self.loadingEl) self.loadingEl.setAttribute('hidden', '');
            reflectToggles(self.grid);
          });
        }
      }
    );
  }

  /*
   * Variant selection controller for the drawer body (fetched partial).
   * Reuses the product-variant-picker CSS contract (option radios) rather than the
   * block-coupled snippet, since the drawer is not a PDP section block. Resolves the
   * selected variant against the embedded variants JSON, updates the price + selected
   * value labels, and gates the confirm button (aria-live size validation).
   */
  if (!customElements.get('wishlist-variant-form')) {
    customElements.define(
      'wishlist-variant-form',
      class extends HTMLElement {
        connectedCallback() {
          this.pid = this.getAttribute('data-product-id');
          this.handle = this.getAttribute('data-product-handle');
          this.optionCount = parseInt(this.getAttribute('data-option-count'), 10) || 0;
          var dataEl = this.querySelector('[data-wl-variants]');
          try {
            this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
          } catch (e) {
            this.variants = [];
          }
          this.priceEl = this.querySelector('[data-wl-price]');
          this.imageEl = this.querySelector('[data-wl-image]');
          this.errorEl = this.querySelector('[data-wl-size-error]');
          this.confirmBtn = this.querySelector('[data-wl-confirm]');
          this.qtyInput = this.querySelector('[data-wl-qty] input, input[name="quantity"]');
          this.selected = null;

          this.addEventListener('change', this._onChange.bind(this));
          if (this.confirmBtn) this.confirmBtn.addEventListener('click', this._onConfirm.bind(this));

          /* Pre-select a variant when editing. */
          var preset = this.getAttribute('data-variant-id');
          if (preset) this._preselect(preset);
          this._recompute();
        }

        get mode() {
          var drawer = this.closest('[data-wl-mode]');
          return drawer ? drawer.getAttribute('data-wl-mode') : 'add';
        }

        _selectedValues() {
          var values = [];
          for (var pos = 1; pos <= this.optionCount; pos++) {
            var checked = this.querySelector('input[data-option-position="' + pos + '"]:checked');
            values.push(checked ? checked.value : null);
          }
          return values;
        }

        _preselect(variantId) {
          var variant = this.variants.filter(function (v) {
            return String(v.id) === String(variantId);
          })[0];
          if (!variant || !variant.options) return;
          var self = this;
          variant.options.forEach(function (val, i) {
            var input = self.querySelector(
              'input[data-option-position="' + (i + 1) + '"][value="' + CSS.escape(val) + '"]'
            );
            if (input) input.checked = true;
          });
        }

        _onChange(e) {
          if (!e.target.matches('[data-option-position]')) return;
          this._recompute();
        }

        _recompute() {
          var values = this._selectedValues();
          var complete = values.every(function (v) {
            return v !== null;
          });
          this.selected = null;
          if (complete) {
            this.selected = this.variants.filter(function (v) {
              return (
                v.options &&
                v.options.length === values.length &&
                v.options.every(function (o, i) {
                  return o === values[i];
                })
              );
            })[0];
          }
          /* Update selected-value labels. */
          var self = this;
          this.querySelectorAll('[data-wl-selected-value]').forEach(function (el) {
            var pos = parseInt(el.getAttribute('data-wl-selected-value'), 10);
            el.textContent = values[pos - 1] || el.getAttribute('data-placeholder') || '';
          });
          /* Update price + image when a concrete variant resolves. */
          if (this.selected) {
            if (this.priceEl && this.selected.price) this.priceEl.textContent = this.selected.price;
            if (this.imageEl && this.selected.image) this.imageEl.src = this.selected.image;
          }
          var ready = this.optionCount === 0 || (complete && this.selected && this.selected.available !== false);
          if (this.confirmBtn) this.confirmBtn.disabled = !ready;
          if (ready && this.errorEl) this.errorEl.setAttribute('hidden', '');
          void self;
        }

        _onConfirm(e) {
          e.preventDefault();
          if (this.optionCount > 0 && !this.selected) {
            if (this.errorEl) this.errorEl.removeAttribute('hidden');
            return;
          }
          var qty = this.qtyInput ? parseInt(this.qtyInput.value, 10) || 1 : 1;
          Store.upsertVariant({
            pid: this.pid,
            handle: this.handle,
            id: this.selected ? this.selected.id : null,
            qty: qty,
          });
          var drawer = this.closest('.drawer');
          if (drawer && typeof drawer.hide === 'function') drawer.hide();
        }
      }
    );
  }

  /* Drawer controller - opens Add / Edit drawers and fills them via Section Rendering. */
  var DRAWERS = { add: 'AddWishlistDrawer', edit: 'EditVariantDrawer' };

  function openWishlistDrawer(mode, trigger) {
    var drawer = document.getElementById(DRAWERS[mode]);
    if (!drawer) return;
    var handle = trigger.getAttribute('data-product-handle');
    var variantId = trigger.getAttribute('data-variant-id');
    var body = drawer.querySelector('[data-wl-drawer-body]');
    if (!handle || !body) return;
    var formSection = body.getAttribute('data-form-section');
    if (!formSection) return;

    drawer.setAttribute('data-wl-mode', mode);
    body.setAttribute('aria-busy', 'true');

    fetchProductSection(handle, formSection)
      .then(function (html) {
        var node = parseFragment(html, '[data-wl-form-root]');
        body.innerHTML = '';
        if (node) {
          if (mode === 'edit' && variantId) node.setAttribute('data-variant-id', variantId);
          node.setAttribute('data-wl-form-mode', mode);
          body.appendChild(node);
        }
        body.removeAttribute('aria-busy');
        if (typeof drawer.show === 'function') drawer.show(trigger);
      })
      .catch(function () {
        body.removeAttribute('aria-busy');
      });
  }

  document.addEventListener('click', function (e) {
    var edit = e.target.closest('[data-wl-edit]');
    if (edit) {
      e.preventDefault();
      openWishlistDrawer('edit', edit);
      return;
    }
    var add = e.target.closest('[data-wl-add-trigger]');
    if (add) {
      e.preventDefault();
      openWishlistDrawer('add', add);
    }
  });

  /* Boot: hydrate from the (guarded) customer metafield, then reflect UI. */
  function boot() {
    var mfEl = document.querySelector('[data-wl-metafield]');
    if (mfEl) {
      try {
        Store.hydrateFromMetafield(JSON.parse(mfEl.textContent));
      } catch (e) {
        /* malformed metafield JSON - ignore, keep localStorage */
      }
    }
    reflectToggles(document);
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { items: Store.items() } }));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
